
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface WeightRecommendation {
  category: string;
  rangeKg: [number, number];
  rangeLbs: [number, number];
  description: string;
  className: string;
}

const PregnancyWeightCalculator = () => {
  const [height, setHeight] = useState('');
  const [prePregnancyWeight, setPrePregnancyWeight] = useState('');
  const [currentWeight, setCurrentWeight] = useState('');
  const [weeks, setWeeks] = useState('');
  const [unit, setUnit] = useState('kg');
  const [results, setResults] = useState<{
    bmi?: number;
    bmiCategory?: string;
    recommendedGain?: WeightRecommendation;
    currentGain?: number;
    expectedGain?: number;
    isOnTrack?: boolean;
    message?: string;
  }>({});

  const calculateBMI = (weight: number, heightCm: number) => {
    const heightM = heightCm / 100;
    return weight / (heightM * heightM);
  };

  const getBMICategory = (bmi: number) => {
    if (bmi < 18.5) return 'Underweight';
    if (bmi < 25) return 'Normal weight';
    if (bmi < 30) return 'Overweight';
    return 'Obese';
  };

  const getWeightRecommendations = (bmiCategory: string): WeightRecommendation => {
    switch (bmiCategory) {
      case 'Underweight':
        return {
          category: 'Underweight',
          rangeKg: [12.5, 18],
          rangeLbs: [28, 40],
          description: 'Recommended weight gain for underweight women (BMI < 18.5)',
          className: 'bg-blue-100 text-blue-800'
        };
      case 'Normal weight':
        return {
          category: 'Normal weight',
          rangeKg: [11.5, 16],
          rangeLbs: [25, 35],
          description: 'Recommended weight gain for normal weight women (BMI 18.5-24.9)',
          className: 'bg-green-100 text-green-800'
        };
      case 'Overweight':
        return {
          category: 'Overweight',
          rangeKg: [7, 11.5],
          rangeLbs: [15, 25],
          description: 'Recommended weight gain for overweight women (BMI 25-29.9)',
          className: 'bg-yellow-100 text-yellow-800'
        };
      case 'Obese':
        return {
          category: 'Obese',
          rangeKg: [5, 9],
          rangeLbs: [11, 20],
          description: 'Recommended weight gain for obese women (BMI ≥ 30)',
          className: 'bg-orange-100 text-orange-800'
        };
      default:
        return {
          category: 'Unknown',
          rangeKg: [0, 0],
          rangeLbs: [0, 0],
          description: 'BMI category unknown',
          className: 'bg-gray-100 text-gray-800'
        };
    }
  };

  // Weight gain pattern approximation (simplified for this calculator)
  const getExpectedWeightGainAtWeek = (totalGain: number, currentWeek: number) => {
    // First trimester (weeks 1-12): ~10% of total weight gain
    // Second trimester (weeks 13-26): ~40% of total gain
    // Third trimester (weeks 27-40): ~50% of total gain
    
    if (currentWeek <= 12) {
      return (currentWeek / 12) * (totalGain * 0.1);
    } else if (currentWeek <= 26) {
      const firstTrimesterGain = totalGain * 0.1;
      const secondTrimesterProgress = (currentWeek - 12) / 14;
      return firstTrimesterGain + (secondTrimesterProgress * (totalGain * 0.4));
    } else {
      const firstTrimesterGain = totalGain * 0.1;
      const secondTrimesterGain = totalGain * 0.4;
      const thirdTrimesterProgress = Math.min((currentWeek - 26) / 14, 1);
      return firstTrimesterGain + secondTrimesterGain + (thirdTrimesterProgress * (totalGain * 0.5));
    }
  };

  const handleCalculate = () => {
    try {
      // Convert inputs to numbers
      const heightValue = parseFloat(height);
      const preWeightValue = parseFloat(prePregnancyWeight);
      const currentWeightValue = currentWeight ? parseFloat(currentWeight) : 0;
      const weeksValue = weeks ? parseInt(weeks) : 0;

      // Validate inputs
      if (isNaN(heightValue) || isNaN(preWeightValue)) {
        setResults({ message: 'Please enter valid height and pre-pregnancy weight' });
        return;
      }

      // Calculate BMI based on pre-pregnancy weight
      const bmi = calculateBMI(
        unit === 'lbs' ? preWeightValue * 0.453592 : preWeightValue, 
        heightValue
      );
      
      const bmiCategory = getBMICategory(bmi);
      const recommendedGain = getWeightRecommendations(bmiCategory);

      // Calculate current weight gain if available
      if (currentWeightValue && weeksValue) {
        const currentGain = currentWeightValue - preWeightValue;
        
        // Get median of recommended weight gain
        const medianGain = unit === 'kg' 
          ? (recommendedGain.rangeKg[0] + recommendedGain.rangeKg[1]) / 2
          : (recommendedGain.rangeLbs[0] + recommendedGain.rangeLbs[1]) / 2;
        
        // Calculate expected gain at current week
        const expectedGain = getExpectedWeightGainAtWeek(medianGain, weeksValue);
        
        // Determine if on track (within 20% of expected)
        const isOnTrack = currentGain >= expectedGain * 0.8 && currentGain <= expectedGain * 1.2;
        
        setResults({
          bmi: bmi,
          bmiCategory: bmiCategory,
          recommendedGain: recommendedGain,
          currentGain: currentGain,
          expectedGain: expectedGain,
          isOnTrack: isOnTrack
        });
      } else {
        // If no current weight data, just show recommendations
        setResults({
          bmi: bmi,
          bmiCategory: bmiCategory,
          recommendedGain: recommendedGain
        });
      }
    } catch (error) {
      setResults({ message: 'Error calculating pregnancy weight data' });
    }
  };

  return (
    <Card className="w-full">
      <CardContent className="pt-6">
        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Pregnancy Weight Gain Calculator</h3>
            <p className="text-sm text-muted-foreground">
              Calculate recommended weight gain during pregnancy based on your pre-pregnancy BMI.
            </p>
          </div>
          
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="unit">Unit</Label>
              <Select value={unit} onValueChange={setUnit}>
                <SelectTrigger>
                  <SelectValue placeholder="Select unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="kg">Kilograms (kg) / Centimeters (cm)</SelectItem>
                  <SelectItem value="lbs">Pounds (lbs) / Inches (in)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="height">
                Height {unit === 'kg' ? '(cm)' : '(in)'}
              </Label>
              <Input
                id="height"
                type="number"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder={unit === 'kg' ? 'e.g., 165' : 'e.g., 65'}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="preWeight">
                Pre-pregnancy Weight {unit === 'kg' ? '(kg)' : '(lbs)'}
              </Label>
              <Input
                id="preWeight"
                type="number"
                value={prePregnancyWeight}
                onChange={(e) => setPrePregnancyWeight(e.target.value)}
                placeholder={unit === 'kg' ? 'e.g., 60' : 'e.g., 132'}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="currentWeight">
                Current Weight {unit === 'kg' ? '(kg)' : '(lbs)'} (Optional)
              </Label>
              <Input
                id="currentWeight"
                type="number"
                value={currentWeight}
                onChange={(e) => setCurrentWeight(e.target.value)}
                placeholder={unit === 'kg' ? 'e.g., 65' : 'e.g., 143'}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="weeks">Current Week of Pregnancy (Optional)</Label>
              <Input
                id="weeks"
                type="number"
                value={weeks}
                onChange={(e) => setWeeks(e.target.value)}
                placeholder="e.g., 20"
                min="1"
                max="42"
              />
            </div>
            
            <Button onClick={handleCalculate} className="mt-2">
              Calculate
            </Button>
          </div>

          {results.message ? (
            <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-md">
              {results.message}
            </div>
          ) : results.bmi ? (
            <div className="mt-6 space-y-4">
              <div className="p-4 border rounded-md">
                <h4 className="text-lg font-medium mb-4">Your Results</h4>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium">Pre-pregnancy BMI:</p>
                    <p className="text-lg">
                      {results.bmi.toFixed(1)}
                      <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${results.bmiCategory === 'Normal weight' ? 'bg-green-100 text-green-800' : results.bmiCategory === 'Underweight' ? 'bg-blue-100 text-blue-800' : results.bmiCategory === 'Overweight' ? 'bg-yellow-100 text-yellow-800' : 'bg-orange-100 text-orange-800'}`}>
                        {results.bmiCategory}
                      </span>
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-sm font-medium">Recommended Weight Gain:</p>
                    <p className="text-lg">
                      {unit === 'kg' 
                        ? `${results.recommendedGain?.rangeKg[0]} - ${results.recommendedGain?.rangeKg[1]} kg`
                        : `${results.recommendedGain?.rangeLbs[0]} - ${results.recommendedGain?.rangeLbs[1]} lbs`}
                    </p>
                  </div>
                  
                  {results.currentGain !== undefined && (
                    <div>
                      <p className="text-sm font-medium">Your Current Weight Gain:</p>
                      <p className="text-lg">
                        {results.currentGain.toFixed(1)} {unit}
                        {results.isOnTrack !== undefined && (
                          <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${results.isOnTrack ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {results.isOnTrack ? 'On Track' : 'Review with Doctor'}
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                  
                  {results.expectedGain !== undefined && (
                    <div>
                      <p className="text-sm font-medium">Expected Weight Gain at Week {weeks}:</p>
                      <p>{results.expectedGain.toFixed(1)} {unit}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="text-sm text-muted-foreground">
                <p>Note: This calculator provides general guidance and should not replace medical advice. Always consult your healthcare provider for personalized recommendations.</p>
              </div>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
};

export default PregnancyWeightCalculator;
